import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { required } from 'vuelidate/lib/validators';

import { Country, CountrySuggest, Industry, IndustrySuggest, Role, RoleSuggest } from '@model/suggestions';

import { checkTenant } from '@api/employerRegistration';
import { suggestCountries, suggestIndustries, suggestJobRoles } from '@api/suggestions';
import { matchError } from '@api/util';
import { OrganizationRegistration } from '@model/employer';
import { hasString } from '../../../util/index';

import WithRender from './AboutCompany.html';

type JIndustry = IndustrySuggest & { meta?: Industry };
type JRole = RoleSuggest & { meta?: Role };

const DEFAULT_ROLE: JRole = {
    value: '',
    id: -1,
    jobFunctionId: -1
};

const DEFAULT_INDUSTRY: JIndustry = {
    id: -1,
    value: ''
};

const DEFAULT_COUNTRY: CountrySuggest = {
    id: -1,
    value: '',
    dialCode: -1
};

@WithRender
@Component({
    validations: {
        companyName: { required },
        selectedCountry: { required: (country: CountrySuggest) => country.id !== -1},
        industry: { required: (industry: JIndustry) => industry.id !== -1 },
        employeesCount: {
            required,
            isValid: (count: number) => count > 0
        }
        // role: { required }
    }
})
export default class AboutCompany extends Vue {

    @Prop()
    public orgData: OrganizationRegistration;

    public $v: any;

    public errorMessage: string = '';

    public unknownError: boolean = false;

    public allIndustries: IndustrySuggest[] = [];

    public allRoles: RoleSuggest[] = [];

    public allCountries: Country[] = [];

    public ifTenantExists: boolean = false;

    public checking: boolean = false;

    public checkDone: boolean = false;

    // Search Strings
    public industrySearch: string = '';

    public roleSearch: string = '';

    public countrySearch: string = '';

    // User selected values
    public employeesCount: number = 0;

    public companyName: string = '';

    public industry: JIndustry = DEFAULT_INDUSTRY;

    public role: JRole = DEFAULT_ROLE;

    public selectedCountry: CountrySuggest = DEFAULT_COUNTRY;

    public get industryOpts() {
        return this.allIndustries
            .filter((x) => hasString(x.value, this.industrySearch));
    }

    public get roleOpts() {
        return this.allRoles
            .filter((x) => hasString(x.value, this.roleSearch));
    }

    public get countryOpts() {
        return this.allCountries
            .filter((x) => hasString(x.value, this.countrySearch));
    }

    public created() {

        this.companyName = this.orgData.name;
        this.employeesCount = this.orgData.numberOfEmployees;
        // this.role = this.orgData.role;
        this.industry = this.orgData.industry;
        this.selectedCountry = this.orgData.country;

        suggestIndustries()
            .then((data) => this.allIndustries = data)
            .catch(matchError({
                anyError: () => {
                    const message = 'Something went wrong!';

                    this.errorMessage = message;
                    this.unknownError = true;
                    this.$snackbar.error({ message });
                }
            }));

        // suggestJobRoles()
        //     .then((data) => this.allRoles = data)
        //     .catch(matchError({
        //         anyError: () => {
        //             const message = 'Something went wrong!';

        //             this.errorMessage = message;
        //             this.unknownError = true;
        //             this.$snackbar.error({ message });
        //         }
        //     }));

        suggestCountries()
            .then((data) => this.allCountries = data.reverse())
            .catch(matchError({
                anyError: () => {
                    const message = 'Something went wrong!';

                    this.errorMessage = message;
                    this.unknownError = true;
                    this.$snackbar.error({ message });
                }
            }));
    }

    public checkIfTenantExists() {
        this.checkDone = false;

        if (this.companyName.trim().length) {

            if (!this.$v.companyName.required) {
                return;
            }

            this.checking = true;

            checkTenant(this.companyName)
                .then((data) => {
                    this.checking = false;
                    this.ifTenantExists = data;
                })
                .catch(matchError({
                    anyError: () => {
                        const message = 'Something went wrong! checkTenant err';

                        this.checking = false;
                        this.errorMessage = message;
                        this.unknownError = true;
                        this.$snackbar.error({ message });
                    }
                }))
                .then((x) => {
                    this.checking = false;
                    this.checkDone = true;
                });
        }
    }

    public hasError(model: string) {
        return this.$v.$dirty && this.$v[model].$error;
    }

    public onSubmit() {
        // Dirty all inputs
        this.$v.$touch();

        if (this.$v.$invalid || this.ifTenantExists) {
            return;
        }

        const data = {
            name: this.companyName,
            country: this.selectedCountry,
            industry: this.industry,
            count: this.employeesCount
            // role: this.role
        };

        this.$emit('next', 2, data);
    }

    public onSearch(key: string, q: string) {
        // key is one of the search string:
        // industrySearch, roleSearch
        this[key] = q;
    }

    public toText(x: JIndustry | JRole) {
        return x.value;
    }
}
